/**
 * Composant de checklist pour afficher la progression de l'onboarding
 * Montre les éléments collectés et ceux qui restent à compléter
 * 
 * APPROCHE I18N: Utilise useLanguage() pour toutes les traductions
 */

import { useState } from 'react';
import { Check, Circle, Loader2, ChevronDown } from 'lucide-react';
import { OnboardingData, OnboardingStepId } from '../../types/positioning';
import { useLanguage } from '../../contexts/LanguageContext';

interface OnboardingChecklistProps {
  data: OnboardingData;
  currentStep: OnboardingStepId;
  isActive: boolean;
}

interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
  current: boolean;
  value?: string;
}

export function OnboardingChecklist({ data, currentStep, isActive }: OnboardingChecklistProps) {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isActive) return null;

  // Récupérer les labels traduits (utilise les clés i18n, pas de fallback français)
  // Si une clé manque, afficher la clé pour faciliter le debug
  const checklist = t.onboarding?.checklist ?? {
    progression: '[onboarding.checklist.progression]',
    usageType: '[onboarding.checklist.usageType]',
    identityCompany: '[onboarding.checklist.identityCompany]',
    identityPersonal: '[onboarding.checklist.identityPersonal]',
    purpose: '[onboarding.checklist.purpose]',
    tone: '[onboarding.checklist.tone]',
    values: '[onboarding.checklist.values]',
    audience: '[onboarding.checklist.audience]',
    personal: '[onboarding.checklist.personal]',
    company: '[onboarding.checklist.company]',
  };

  const checklistShort = t.onboarding?.checklistShort ?? {
    brand_awareness: '[checklistShort.brand_awareness]',
    thought_leadership: '[checklistShort.thought_leadership]',
    lead_generation: '[checklistShort.lead_generation]',
    community: '[checklistShort.community]',
    recruitment: '[checklistShort.recruitment]',
    other: '[checklistShort.other]',
    professional: '[checklistShort.professional]',
    friendly: '[checklistShort.friendly]',
    inspiring: '[checklistShort.inspiring]',
    provocative: '[checklistShort.provocative]',
    educational: '[checklistShort.educational]',
    humorous: '[checklistShort.humorous]',
  };

  // Helper pour récupérer le label traduit d'un objectif
  const getPurposeLabel = (purpose?: string): string | undefined => {
    if (!purpose) return undefined;
    return checklistShort[purpose as keyof typeof checklistShort] || purpose;
  };

  // Helper pour récupérer le label traduit d'un ton
  const getToneLabel = (tone?: string): string | undefined => {
    if (!tone) return undefined;
    return checklistShort[tone as keyof typeof checklistShort] || tone;
  };

  // Définir les items de la checklist avec labels traduits
  const items: ChecklistItem[] = [
    {
      id: 'usage_type',
      label: checklist.usageType,
      completed: !!data.usageType,
      current: currentStep === 'usage_type',
      value: data.usageType === 'personal' 
        ? checklist.personal 
        : data.usageType === 'company' 
          ? checklist.company 
          : undefined,
    },
    {
      id: 'identity',
      label: data.usageType === 'company' ? checklist.identityCompany : checklist.identityPersonal,
      completed: !!(data.companyName || data.personalDomain || data.extractedData?.companyName),
      current: currentStep === 'company_url' || currentStep === 'company_name' || currentStep === 'personal_domain',
      value: data.companyName || data.extractedData?.companyName || data.personalDomain,
    },
    {
      id: 'purpose',
      label: checklist.purpose,
      completed: !!data.purpose,
      current: currentStep === 'purpose',
      value: getPurposeLabel(data.purpose),
    },
    {
      id: 'tone',
      label: checklist.tone,
      completed: !!data.tone,
      current: currentStep === 'tone',
      value: getToneLabel(data.tone),
    },
    {
      id: 'values',
      label: checklist.values,
      completed: !!(data.values && data.values.length > 0),
      current: currentStep === 'values',
      value: data.values && data.values.length > 0
        ? data.values.slice(0, 2).join(', ') + (data.values.length > 2 ? '...' : '')
        : undefined,
    },
    {
      id: 'audience',
      label: checklist.audience,
      completed: !!data.audience,
      current: currentStep === 'audience',
      value: data.audience ? data.audience.substring(0, 30) + (data.audience.length > 30 ? '...' : '') : undefined,
    },
  ];

  // Filtrer les items non pertinents (ex: si personal, pas besoin de company)
  const filteredItems = items.filter(item => {
    if (item.id === 'identity' && !data.usageType) return true;
    return true;
  });

  return (
    <div
      className="rounded-lg mb-4 border overflow-hidden"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border)'
      }}
    >
      {/* Header cliquable — toujours visible */}
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="text-xs font-medium flex-1" style={{ color: 'var(--color-text-secondary)' }}>
          {checklist.progression}
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: 'var(--color-primary-light)',
            color: 'var(--color-primary)'
          }}
        >
          {filteredItems.filter(i => i.completed).length}/{filteredItems.length}
        </span>
        <ChevronDown
          className="w-3 h-3 transition-transform"
          style={{
            color: 'var(--color-text-secondary)',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {/* UX backlog 9.2 : progress bar visuelle sous le compteur textuel. */}
      <div
        className="h-0.5 mx-3 mb-1 rounded-full overflow-hidden"
        style={{ backgroundColor: 'var(--color-border)' }}
      >
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${(filteredItems.filter(i => i.completed).length / Math.max(filteredItems.length, 1)) * 100}%`,
            backgroundColor: 'var(--color-primary)',
          }}
        />
      </div>

      {/* Items détaillés — visibles uniquement si déplié */}
      {isExpanded && (
      <div className="px-3 pb-3 space-y-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <div className="pt-2 space-y-2">
        {filteredItems.map((item) => (
          <div 
            key={item.id}
            className="flex items-center gap-2"
          >
            {/* Icône de statut */}
            {item.completed ? (
              <div 
                className="w-5 h-5 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-success-light)' }}
              >
                <Check className="w-3 h-3" style={{ color: 'var(--color-success)' }} />
              </div>
            ) : item.current ? (
              <div 
                className="w-5 h-5 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-primary-light)' }}
              >
                <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--color-primary)' }} />
              </div>
            ) : (
              <div 
                className="w-5 h-5 rounded-full flex items-center justify-center border"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <Circle className="w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />
              </div>
            )}
            
            {/* Label et valeur */}
            <div className="flex-1 flex items-center justify-between">
              <span 
                className="text-xs"
                style={{ 
                  color: item.completed ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  fontWeight: item.current ? 500 : 400
                }}
              >
                {item.label}
              </span>
              {item.value && (
                <span 
                  className="text-xs truncate max-w-[120px]"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {item.value}
                </span>
              )}
            </div>
          </div>
        ))}
        </div>
      </div>
      )}
    </div>
  );
}
